/**
 * Christmas door signs — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_seasons`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const christmasDoorSignsPack: ToilePack = {
    slug: 'christmas-door-signs',
    category: 'season',
    name: 'Christmas door signs',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 832, h: 1216},
    readings: [
        {key: 'welcome', label: 'Welcome', subject: 'A vertical door sign lettered "WELCOME" with a wreath below, a thick border'},
        {key: 'santa', label: 'Santa stop here', subject: 'A vertical door sign lettered "SANTA STOP HERE" in bold with an arrow and a chimney motif, a thick border'},
        {key: 'elves', label: 'Elf notice', subject: 'A vertical door sign lettered "ELF WORKSHOP" with a small striped candy border'},
    ],
    styles: [
        {key: 'classic', label: 'Classic', style: 'classic christmas printable, holly and berry borders, warm serif lettering, print-ready', palette: 'Deep red, pine green, cream and gold accents', framing: 'Laid out on a vertical page with an even white margin'},
        {key: 'nordic', label: 'Nordic', style: 'nordic minimal printable, thin line motifs, generous white space, print-ready', palette: 'Red and white with a single charcoal line', framing: 'Laid out on a clean page with wide margins'},
        {key: 'kids', label: 'Kids', style: "kids' christmas printable, bright cartoon motifs, rounded lettering, print-ready", palette: 'Bright red, green, sky blue and yellow', framing: 'Laid out on a vertical page with a playful border and a white margin'},
    ],
    directions: ['for the office door', 'as a round wreath sign', 'with a name blank'],
    directionHint: 'try: with a name blank',
    seo: {
        title: 'Christmas Door Sign Printable: Welcome, Santa, Elf',
        metaDescription: 'Nine printable christmas door signs — Welcome, Santa Stop Here, Elf Workshop — in classic, nordic and kids styles, print-ready.',
        h1: 'Christmas door sign printable: welcome, Santa stop here, elf notice',
        tagline: 'Nine signs, three door messages, three styles each.',
        intro: 'A print-ready door sign in three headlines — "Welcome" over a wreath, "Santa Stop Here" with an arrow and chimney, "Elf Workshop" over a candy-cane border — each drawn classic, nordic minimal, and bright for kids. Ask for an office-door version, a round wreath sign, or a blank name line.',
        useCases: [
            'a front-door christmas decoration',
            'an office holiday door sign',
            "a kids' bedroom door notice",
            'a printable holiday welcome sign',
        ],
        faq: [
            {q: 'What does this pack actually look like?', a: 'Nine printable door signs across three headlines — Welcome, Santa Stop Here, Elf Workshop — each in a classic, nordic and kids style, lettered and bordered.'},
            {q: 'Is the headline text always the same, or can I change it?', a: 'The three headlines print exactly as shown; ask for different wording or an added blank name line in the direction field.'},
            {q: 'What should I print and mount this on?', a: 'Card stock or a printed poster sheet both work; laminate it if the sign will hang outdoors.'},
            {q: 'Can I get this for an office door instead of a house?', a: 'Yes — the direction field takes any variation, including an office-door version, a round wreath shape, or a blank line for a name.'},
            {q: 'Is this suitable for outdoor use?', a: "It's designed as a printable sign, not weatherproof material — laminating or placing it under glass protects it outdoors."},
        ],
    },
}
